/**
 * ============================================================================
 * GROUPED TIME SLOT CALENDAR - TABBED TIMELINE VIEW
 * ============================================================================
 *
 * Displays a parent court with its child courts using a single timeline
 * with filter tabs. Shows the physical reality of the court.
 *
 * Design matches TimeSlotCalendar exactly, with added tabs for filtering.
 *
 * ============================================================================
 */

import React, { useState, useMemo } from 'react';
import { Calendar, Lock, CheckCircle, RefreshCw, X, Clock } from 'lucide-react';

interface TimeSlot {
  time: string;
  start_time: string;
  end_time: string;
  status: 'available' | 'booked' | 'blocked' | 'unavailable';
  reference?: string | null;
  reason?: string | null;
  block_id?: number | null;
  partner_id?: number | null;
  partner_name?: string | null;
  is_partner_block?: boolean;
  blocked_by?: string | null;
}

interface ScheduleData {
  total_slots: number;
  available: number;
  booked: number;
  blocked: number;
  unavailable?: number;
  slots: TimeSlot[];
}

interface Court {
  id: number;
  name: string;
  parent_court_id?: number | null;
  court_number?: number;
}

interface GroupedTimeSlotCalendarProps {
  parentCourt: Court;
  childCourts: Court[];
  scheduleData: { [courtId: number]: ScheduleData | null };
  isLoading?: boolean;
}

type ViewType = 'all' | number; // 'all' or court ID

interface CourtSlotInfo {
  courtName: string;
  shortName: string;
  status: 'available' | 'booked' | 'blocked' | 'unavailable' | 'partner_blocked';
  reference?: string | null;
  reason?: string | null;
  isPartnerBlock?: boolean;
  partnerName?: string | null;
  blockedBy?: string | null;
}

interface SlotDetails {
  time: string;
  endTime: string;
  courtName: string;
  status: 'available' | 'booked' | 'blocked' | 'unavailable';
  blockedBy?: string;
  blockReason?: string;
  inUseBy?: string;
  reference?: string | null;
  isPartnerBlock?: boolean;
  partnerName?: string | null;
  courtBreakdown?: CourtSlotInfo[];
}

/**
 * Get short name for court by stripping the sport name prefix.
 * e.g. "Basketball Half Court 1" → "Half Court 1"
 */
function getShortName(name: string, isParent: boolean): string {
  if (isParent) return 'Full Court';
  // Strip the first word (sport name): "Basketball Half Court 1" → "Half Court 1"
  const firstSpaceIdx = name.indexOf(' ');
  if (firstSpaceIdx !== -1) {
    return name.substring(firstSpaceIdx + 1);
  }
  return name;
}

/**
 * Main grouped calendar component with tabbed timeline
 */
const GroupedTimeSlotCalendar: React.FC<GroupedTimeSlotCalendarProps> = ({
  parentCourt,
  childCourts,
  scheduleData,
  isLoading = false,
}) => {
  const [activeView, setActiveView] = useState<ViewType>('all');
  const [selectedSlot, setSelectedSlot] = useState<SlotDetails | null>(null);

  // Sort children by court_number
  const sortedChildren = useMemo(
    () => [...childCourts].sort((a, b) => (a.court_number ?? 0) - (b.court_number ?? 0)),
    [childCourts]
  );

  // All courts in order: parent first, then children
  const allCourts = useMemo(() => [parentCourt, ...sortedChildren], [parentCourt, sortedChildren]);

  // Build merged slot data for the timeline
  const timelineData = useMemo(() => {
    const parentSchedule = scheduleData[parentCourt.id];
    if (!parentSchedule || parentSchedule.slots.length === 0) {
      return { slots: [], stats: { available: 0, booked: 0, blocked: 0 } };
    }

    // Build a map of time -> status for each court
    const slotsByTime: Map<
      string,
      {
        time: string;
        start_time: string;
        end_time: string;
        courts: { [courtId: number]: TimeSlot };
      }
    > = new Map();

    // Initialize with parent slots
    parentSchedule.slots.forEach((slot) => {
      slotsByTime.set(slot.start_time, {
        time: slot.time,
        start_time: slot.start_time,
        end_time: slot.end_time,
        courts: { [parentCourt.id]: slot },
      });
    });

    // Add child slots
    sortedChildren.forEach((child) => {
      const childSchedule = scheduleData[child.id];
      if (childSchedule) {
        childSchedule.slots.forEach((slot) => {
          const existing = slotsByTime.get(slot.start_time);
          if (existing) {
            existing.courts[child.id] = slot;
          }
        });
      }
    });

    // Convert to array and calculate stats based on active view
    const slotsArray = Array.from(slotsByTime.values());
    let available = 0;
    let booked = 0;
    let blocked = 0;
    let unavailable = 0;

    slotsArray.forEach((slotData) => {
      if (activeView === 'all') {
        // For "All" view, count based on overall availability
        const parentStatus = slotData.courts[parentCourt.id]?.status;
        const childStatuses = sortedChildren.map((c) => slotData.courts[c.id]?.status);
        const anyBooked = parentStatus === 'booked' || childStatuses.some((s) => s === 'booked');
        const anyBlocked = parentStatus === 'blocked' || childStatuses.some((s) => s === 'blocked');
        const anyUnavailable = parentStatus === 'unavailable' || childStatuses.some((s) => s === 'unavailable');
        const allAvailable =
          parentStatus === 'available' && childStatuses.every((s) => s === 'available' || !s);

        if (anyBooked) booked++;
        else if (anyBlocked) blocked++;
        else if (allAvailable) available++;
        else if (anyUnavailable) unavailable++;
        else blocked++;
      } else {
        // For specific court view
        const courtSlot = slotData.courts[activeView];
        if (courtSlot) {
          if (courtSlot.status === 'available') available++;
          else if (courtSlot.status === 'booked') booked++;
          else if (courtSlot.status === 'unavailable') unavailable++;
          else blocked++;
        }
      }
    });

    return {
      slots: slotsArray,
      stats: { available, booked, blocked, unavailable },
    };
  }, [scheduleData, parentCourt, sortedChildren, activeView]);

  /**
   * Get display info for a slot based on current view
   */
  const getSlotDisplay = (
    slotData: (typeof timelineData.slots)[0]
  ): { status: string; label: string; sublabel?: string; blockedBy?: string; inUseBy?: string; isPartnerBlock?: boolean; partnerName?: string | null } => {
    if (activeView === 'all') {
      // Merged view - show summary across parent + children
      const parentSlot = slotData.courts[parentCourt.id];
      const childSlots = sortedChildren.map((c) => ({
        court: c,
        slot: slotData.courts[c.id],
      }));

      // 1. Parent directly booked → entire court occupied
      if (parentSlot?.status === 'booked') {
        return { status: 'booked', label: 'Full', sublabel: 'Court' };
      }

      // 2. Check booked children
      const bookedChildren = childSlots.filter((cs) => cs.slot?.status === 'booked');
      if (bookedChildren.length === sortedChildren.length && sortedChildren.length > 0) {
        return { status: 'booked', label: 'Both', sublabel: 'Halves' };
      }
      if (bookedChildren.length === 1) {
        return { status: 'booked', label: getShortName(bookedChildren[0].court.name, false) };
      }
      if (bookedChildren.length > 1) {
        return { status: 'booked', label: `${bookedChildren.length}`, sublabel: 'Halves' };
      }

      // 3. Check blocked children (manual blocks / partner blocks)
      const blockedChildren = childSlots.filter((cs) => cs.slot?.status === 'blocked');
      if (blockedChildren.length > 0) {
        // Check if any are partner blocks
        const partnerBlocked = blockedChildren.filter(
          (cs) => cs.slot?.is_partner_block || cs.slot?.reason === 'third_party'
        );
        if (partnerBlocked.length > 0) {
          return {
            status: 'partner_blocked',
            label: '🔗',
            isPartnerBlock: true,
            partnerName: partnerBlocked[0].slot?.partner_name,
          };
        }
        // Manual block on child(ren)
        if (blockedChildren.length === sortedChildren.length && sortedChildren.length > 0) {
          return { status: 'blocked', label: '🔒', blockedBy: 'Both Halves' };
        }
        const names = blockedChildren.map((c) => getShortName(c.court.name, false)).join(', ');
        return { status: 'blocked', label: '🔒', blockedBy: names };
      }

      // 4. Parent directly blocked (manual block on full court)
      if (parentSlot?.status === 'blocked') {
        const isPartner = parentSlot.is_partner_block || parentSlot.reason === 'third_party';
        return { status: isPartner ? 'partner_blocked' : 'blocked', label: isPartner ? '🔗' : '🔒', isPartnerBlock: isPartner, partnerName: parentSlot.partner_name };
      }

      // 5. Check unavailable children (blocked by cross-sport booking)
      const unavailableChildren = childSlots.filter((cs) => cs.slot?.status === 'unavailable');
      if (unavailableChildren.length > 0) {
        const reasons = unavailableChildren
          .map((c) => c.slot?.reason)
          .filter(Boolean);
        const reason = reasons.length > 0 ? reasons[0] : undefined;
        return { status: 'unavailable', label: '⏸️', inUseBy: reason || undefined };
      }

      // 6. Parent unavailable (due to cross-sport blocking)
      if (parentSlot?.status === 'unavailable') {
        return { status: 'unavailable', label: '⏸️', inUseBy: parentSlot.reason || undefined };
      }

      return { status: 'available', label: '✓' };
    } else {
      // Specific court view
      const courtSlot = slotData.courts[activeView];
      const isParentView = activeView === parentCourt.id;

      if (!courtSlot) {
        return { status: 'available', label: '✓' };
      }

      if (courtSlot.status === 'available') {
        return { status: 'available', label: '✓' };
      }

      if (courtSlot.status === 'booked') {
        return { status: 'booked', label: '📅' };
      }

      // Unavailable - blocked due to a related court's booking or block
      if (courtSlot.status === 'unavailable') {
        if (isParentView) {
          // Parent unavailable - check WHY by looking at children
          const blockedChild = sortedChildren.find((c) => slotData.courts[c.id]?.status === 'blocked');
          if (blockedChild) {
            // Child has a manual block → show as blocked, not "in use"
            const isChildPartner = slotData.courts[blockedChild.id]?.is_partner_block || slotData.courts[blockedChild.id]?.reason === 'third_party';
            if (isChildPartner) {
              return { status: 'partner_blocked', label: '🔗', isPartnerBlock: true, partnerName: slotData.courts[blockedChild.id]?.partner_name };
            }
            return { status: 'blocked', label: '🔒', blockedBy: `${getShortName(blockedChild.name, false)} blocked` };
          }
          const bookedChild = sortedChildren.find((c) => slotData.courts[c.id]?.status === 'booked');
          if (bookedChild) {
            return { status: 'unavailable', label: '⏸️', inUseBy: `${getShortName(bookedChild.name, false)} booked` };
          }
        } else {
          // Child unavailable - check WHY by looking at parent
          const parentStatus = slotData.courts[parentCourt.id]?.status;
          if (parentStatus === 'blocked') {
            const isParentPartner = slotData.courts[parentCourt.id]?.is_partner_block || slotData.courts[parentCourt.id]?.reason === 'third_party';
            if (isParentPartner) {
              return { status: 'partner_blocked', label: '🔗', isPartnerBlock: true, partnerName: slotData.courts[parentCourt.id]?.partner_name };
            }
            return { status: 'blocked', label: '🔒', blockedBy: 'Full Court blocked' };
          }
          if (parentStatus === 'booked') {
            return { status: 'unavailable', label: '⏸️', inUseBy: 'Full Court booked' };
          }
        }
        // Fallback for cross-sport or other reasons
        return { status: 'unavailable', label: '⏸️', inUseBy: courtSlot.reason || undefined };
      }

      // Check for partner block
      const isPartner = courtSlot.is_partner_block || courtSlot.reason === 'third_party';
      if (isPartner) {
        return { status: 'partner_blocked', label: '🔗', isPartnerBlock: true, partnerName: courtSlot.partner_name };
      }

      // Blocked - direct manual block on this court or cross-sport block
      if (courtSlot.blocked_by) {
        return { status: 'blocked', label: '🔒', blockedBy: courtSlot.blocked_by };
      }
      return { status: 'blocked', label: '🔒', blockedBy: courtSlot.reason?.replace(/_/g, ' ') || undefined };
    }
  };

  /**
   * Build per-court status info for a slot
   */
  const getCourtSlotInfo = (court: Court, slot: TimeSlot | undefined, isParent: boolean): CourtSlotInfo => {
    const shortName = isParent ? 'Full Court' : getShortName(court.name, false);
    if (!slot) {
      return { courtName: court.name, shortName, status: 'available' };
    }

    const isPartner = slot.is_partner_block || slot.reason === 'third_party';

    return {
      courtName: court.name,
      shortName,
      status: isPartner ? 'partner_blocked' : slot.status,
      reference: slot.reference,
      reason: slot.reason,
      isPartnerBlock: isPartner,
      partnerName: slot.partner_name,
      blockedBy: slot.blocked_by,
    };
  };

  /**
   * Handle slot click to show details
   */
  const handleSlotClick = (slotData: (typeof timelineData.slots)[0]) => {
    const display = getSlotDisplay(slotData);

    if (activeView === 'all') {
      // Build per-court breakdown
      const courtBreakdown: CourtSlotInfo[] = [];
      courtBreakdown.push(getCourtSlotInfo(parentCourt, slotData.courts[parentCourt.id], true));
      sortedChildren.forEach((child) => {
        courtBreakdown.push(getCourtSlotInfo(child, slotData.courts[child.id], false));
      });

      setSelectedSlot({
        time: slotData.start_time,
        endTime: slotData.end_time,
        courtName: parentCourt.name,
        status: (display.status === 'partner_blocked' ? 'blocked' : display.status) as 'available' | 'booked' | 'blocked' | 'unavailable',
        blockedBy: display.blockedBy,
        inUseBy: display.inUseBy,
        isPartnerBlock: display.isPartnerBlock,
        partnerName: display.partnerName,
        courtBreakdown,
      });
    } else {
      // Individual court view
      const courtSlot = slotData.courts[activeView];
      const courtName = allCourts.find((c) => c.id === activeView)?.name ?? 'Court';

      setSelectedSlot({
        time: slotData.start_time,
        endTime: slotData.end_time,
        courtName,
        status: (display.status === 'partner_blocked' ? 'blocked' : display.status) as 'available' | 'booked' | 'blocked' | 'unavailable',
        blockedBy: display.blockedBy,
        blockReason: courtSlot?.blocked_by ? courtSlot?.reason?.replace(/_/g, ' ') : undefined,
        inUseBy: display.inUseBy,
        reference: courtSlot?.reference,
        isPartnerBlock: display.isPartnerBlock,
        partnerName: display.partnerName,
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  // No schedule data
  if (!scheduleData[parentCourt.id] || timelineData.slots.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-gray-900">{parentCourt.name}</h4>
        </div>
        <p className="text-sm text-gray-500">Venue closed today</p>
      </div>
    );
  }

  // Derive the subtitle shown next to the court name based on the active tab
  const activeTabLabel =
    activeView === 'all'
      ? null
      : activeView === parentCourt.id
      ? null
      : allCourts.find((c) => c.id === activeView)?.name ?? null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      {/* Header with title and tabs */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-bold text-gray-900">
          {parentCourt.name}
          {activeTabLabel && (
            <span className="text-gray-400 font-normal"> · {activeTabLabel}</span>
          )}
        </h4>

        {/* Compact Tabs */}
        <div className="flex gap-0.5 bg-gray-100 p-0.5 rounded-md">
          <button
            onClick={() => setActiveView('all')}
            className={`px-2 py-1 text-[10px] font-semibold rounded transition-all ${
              activeView === 'all'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            All
          </button>
          {allCourts.map((court, idx) => (
            <button
              key={court.id}
              onClick={() => setActiveView(court.id)}
              className={`px-2 py-1 text-[10px] font-semibold rounded transition-all ${
                activeView === court.id
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {getShortName(court.name, idx === 0)}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className={`grid gap-2 mb-3 ${(timelineData.stats.unavailable ?? 0) > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
        <div className="bg-green-50 rounded-lg p-2">
          <div className="flex items-center space-x-1">
            <CheckCircle className="h-3 w-3 text-green-600" />
            <span className="text-xs font-semibold text-green-900">
              {timelineData.stats.available} Available
            </span>
          </div>
        </div>
        <div className="bg-blue-50 rounded-lg p-2">
          <div className="flex items-center space-x-1">
            <Calendar className="h-3 w-3 text-blue-600" />
            <span className="text-xs font-semibold text-blue-900">
              {timelineData.stats.booked} Booked
            </span>
          </div>
        </div>
        {(timelineData.stats.unavailable ?? 0) > 0 && (
          <div className="bg-amber-50 rounded-lg p-2">
            <div className="flex items-center space-x-1">
              <Clock className="h-3 w-3 text-amber-600" />
              <span className="text-xs font-semibold text-amber-900">
                {timelineData.stats.unavailable} In Use
              </span>
            </div>
          </div>
        )}
        <div className="bg-red-50 rounded-lg p-2">
          <div className="flex items-center space-x-1">
            <Lock className="h-3 w-3 text-red-600" />
            <span className="text-xs font-semibold text-red-900">
              {timelineData.stats.blocked} Blocked
            </span>
          </div>
        </div>
      </div>

      {/* Horizontal Time Slots - EXACT same design as TimeSlotCalendar */}
      <div className="overflow-x-auto">
        <div className="flex space-x-1 min-w-max pb-2">
          {timelineData.slots.map((slotData, idx) => {
            const display = getSlotDisplay(slotData);
            return (
              <button
                key={idx}
                onClick={() => handleSlotClick(slotData)}
                className={`flex-shrink-0 w-16 rounded-lg p-2 border transition-all hover:opacity-80 ${
                  display.status === 'available'
                    ? 'bg-green-50 border-green-200'
                    : display.status === 'booked'
                    ? 'bg-blue-50 border-blue-200'
                    : display.status === 'unavailable'
                    ? 'bg-amber-50 border-amber-200'
                    : display.status === 'partner_blocked'
                    ? 'bg-teal-50 border-teal-200'
                    : 'bg-red-50 border-red-200'
                }`}
                title={display.isPartnerBlock ? `Third-Party: ${display.partnerName || 'Partner Sync'}` : undefined}
              >
                <div className="text-xs font-bold text-gray-900 text-center">
                  {slotData.start_time}
                </div>
                <div className="text-xs text-gray-600 text-center mt-0.5">
                  {display.sublabel ? (
                    <>
                      <span>{display.label}</span>
                      <span className="block text-[10px]">{display.sublabel}</span>
                    </>
                  ) : (
                    display.label
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Slot Details Modal */}
      {selectedSlot && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={() => setSelectedSlot(null)}
        >
          <div
            className="bg-white rounded-xl p-5 w-[90%] max-w-sm shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h5 className="text-base font-bold text-gray-900">
                  {selectedSlot.time} - {selectedSlot.endTime}
                </h5>
                <p className="text-xs text-gray-500 mt-0.5">{selectedSlot.courtName}</p>
              </div>
              <button
                onClick={() => setSelectedSlot(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Per-court breakdown (All view) */}
            {selectedSlot.courtBreakdown ? (
              <div className="space-y-2">
                {selectedSlot.courtBreakdown.map((court, idx) => {
                  const statusColor =
                    court.status === 'available' ? 'bg-green-50 border-green-200'
                    : court.status === 'booked' ? 'bg-blue-50 border-blue-200'
                    : court.status === 'unavailable' ? 'bg-amber-50 border-amber-200'
                    : court.status === 'partner_blocked' ? 'bg-teal-50 border-teal-200'
                    : 'bg-red-50 border-red-200';

                  const statusTextColor =
                    court.status === 'available' ? 'text-green-700'
                    : court.status === 'booked' ? 'text-blue-700'
                    : court.status === 'unavailable' ? 'text-amber-700'
                    : court.status === 'partner_blocked' ? 'text-teal-700'
                    : 'text-red-700';

                  const statusLabel =
                    court.status === 'available' ? 'Available'
                    : court.status === 'booked' ? 'Booked'
                    : court.status === 'unavailable' ? 'In Use'
                    : court.status === 'partner_blocked' ? 'Third-Party'
                    : 'Blocked';

                  return (
                    <div key={idx} className={`rounded-lg border p-3 ${statusColor}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-900">
                          {court.courtName}
                        </span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusTextColor} bg-white/60`}>
                          {statusLabel}
                        </span>
                      </div>
                      {/* Details row */}
                      {court.status !== 'available' && (
                        <div className="mt-1.5 space-y-0.5">
                          {court.blockedBy && (
                            <p className="text-xs text-red-700">
                              Blocked by: <span className="font-semibold">{court.blockedBy}</span>
                            </p>
                          )}
                          {court.status === 'blocked' && court.reason && (
                            <p className="text-xs text-red-700 capitalize">
                              Reason: {court.reason.replace(/_/g, ' ')}
                            </p>
                          )}
                          {court.reference && (
                            <p className="text-xs text-gray-600">
                              {court.blockedBy ? 'Notes' : 'Ref'}: <span className="font-semibold text-gray-800">{court.reference}</span>
                            </p>
                          )}
                          {court.status === 'unavailable' && court.reason && (
                            <p className="text-xs text-amber-700">
                              In use by: {court.reason}
                            </p>
                          )}
                          {court.isPartnerBlock && (
                            <p className="text-xs text-teal-700">
                              {court.partnerName || 'Partner Sync'}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Single court view */
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Court</span>
                  <span className="font-semibold text-gray-900">{selectedSlot.courtName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  <span
                    className={`font-semibold ${
                      selectedSlot.status === 'available'
                        ? 'text-green-600'
                        : selectedSlot.status === 'booked'
                        ? 'text-blue-600'
                        : selectedSlot.status === 'unavailable'
                        ? 'text-amber-600'
                        : 'text-red-600'
                    }`}
                  >
                    {selectedSlot.status === 'available'
                      ? 'Available'
                      : selectedSlot.status === 'booked'
                      ? 'Booked'
                      : selectedSlot.status === 'unavailable'
                      ? 'In Use'
                      : 'Blocked'}
                  </span>
                </div>
                {selectedSlot.inUseBy && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reason</span>
                    <span className="font-semibold text-amber-600">{selectedSlot.inUseBy}</span>
                  </div>
                )}
                {selectedSlot.isPartnerBlock && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Source</span>
                    <span className="font-semibold text-teal-600">{selectedSlot.partnerName || 'Partner Sync'}</span>
                  </div>
                )}
                {selectedSlot.blockedBy && !selectedSlot.isPartnerBlock && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Blocked By</span>
                    <span className="font-semibold text-red-600">{selectedSlot.blockedBy}</span>
                  </div>
                )}
                {selectedSlot.blockReason && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reason</span>
                    <span className="font-semibold text-red-600 capitalize">{selectedSlot.blockReason}</span>
                  </div>
                )}
                {selectedSlot.reference && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reference</span>
                    <span className="font-semibold text-gray-900">{selectedSlot.reference}</span>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => setSelectedSlot(null)}
              className="w-full mt-4 bg-gray-900 text-white py-2.5 rounded-lg font-semibold hover:bg-gray-800 transition-colors text-sm"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupedTimeSlotCalendar;
